import BaseWrapper from "../../components/ui/BaseWrapper";
import dayjs from "dayjs";
import BaseRating from "../../components/ui/BaseRating";
import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import { CurrencySymbol, ModuleType, PaymentType, UserType } from "../../utils/enum";
import { fetchWorkforce } from "../../services/api/workforce";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { setWorkforceFilters } from "../../store/filters/workforceFiltersSlice";
import { setFreelanceCompanyWorkforceFilters } from "../../store/filters/freelanceCompanyWorkforceFiltersSlice";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { Avatar, Popover, RadioGroup } from "@mui/material";
import { useRef, useState, useMemo, useEffect, useCallback } from "react";
import BaseButton from "../../components/ui/BaseButton";
import BasePopover from "../../components/ui/BasePopover";
import BaseRadio from "../../components/ui/BaseRadio";
import BaseTextField from "../../components/ui/BaseTextField";
import BaseAutoSelect from "../../components/ui/BaseAutoSelect";
import {
  addEngineerToTalentPool,
  createTalentPool,
  fetchTalentPool,
} from "../../services/api/talentPool";
import { showToast } from "../../store/toast/toastSlice";
import { useNavigate } from "react-router-dom";
import { sortByPaymentType } from "../../utils/sortByPaymentType";
import { debounce } from "../../utils/helpers";
import WorkForceFilters from "./WorkForceFilters";
import useAuth from "../../hooks/useAuth";
import OpenableLink from "../../components/ui/OpenableLink";

const relativeTime = require("dayjs/plugin/relativeTime");
dayjs.extend(relativeTime);

const CUSTOMER_HIDDEN_WORKFORCE_FIELDS = [
  "freelanceCompany",
  "billingRates",
  "phoneNumber",
  "email",
  "workedWithJobsCount",
  "userTalentPools",
  "cancellationRate",
  "timelinessRate",
  "workedWith",
  "backgroundChecked",
  "lastActive",
];

const WorkForceTable = ({ freelanceCompanyId } = {}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getPermissionForModule, authRole } = useAuth();
  const isCustomer = authRole === UserType.CUSTOMER;
  const { hasWriteAccess: hasTalentPoolWriteAccess } = getPermissionForModule(
    ModuleType.WORK_FORCE_TALENT_POOL,
  );

  const { hasUpdateAccess: hasWorkForceEngineerUpdateAccess } = getPermissionForModule(
    ModuleType.WORK_FORCE_ENGINEERS,
  );

  const [selectedEngineers, setSelectedEngineers] = useState([]);
  const [addToTalentPoolPopoverOpen, setAddToTalentPoolPopoverOpen] = useState(null);
  const [talentPoolMode, setTalentPoolMode] = useState("");
  const [talentPoolName, setTalentPoolName] = useState("");
  const [search, setSearch] = useState("");

  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [filtersChanged, setFiltersChanged] = useState(false);

  const isScoped = !!freelanceCompanyId;
  const reduxWorkforceFilters = useSelector((store) => store.workforceFilters.value);
  const reduxFreelanceCompanyFilters = useSelector(
    (store) => store.freelanceCompanyWorkforceFilters.value,
  );
  const workforceFilters = isScoped ? reduxFreelanceCompanyFilters : reduxWorkforceFilters;
  const pagination = workforceFilters.pagination || { page: 0, pageSize: 10 };

  const updateFilter = useCallback(
    ({ filterName, data }) => {
      const action = isScoped ? setFreelanceCompanyWorkforceFilters : setWorkforceFilters;
      dispatch(action({ filterName, data }));
    },
    [isScoped, dispatch],
  );

  const queryParams = useMemo(
    () => ({
      page: pagination.page + 1,
      limit: pagination.pageSize,
      search: workforceFilters.search || "",
      ...(freelanceCompanyId ? { freelanceCompanyId } : {}),
    }),
    [pagination.page, pagination.pageSize, workforceFilters.search, freelanceCompanyId],
  );

  const {
    isPending: isFetchTalentPoolApiLoading,
    data: talentPoolData,
    refetch: refetchTalentPool,
  } = useQuery({
    queryKey: ["talnetPool"],
    queryFn: () => fetchTalentPool(),
    keepPreviousData: true,
    enabled: !isCustomer,
  });

  const formattedTalentPoolOptions = talentPoolData?.data?.data?.map((talentPool) => {
    return {
      label: talentPool?.name,
      value: talentPool?.id,
    };
  });

  const filtersPayload = useMemo(() => {
    return {
      country: workforceFilters.country?.label || undefined,
      city: workforceFilters.city?.value || undefined,
      fullName: workforceFilters?.fullName || undefined,
      talentPool: workforceFilters.talentPool?.value || undefined,
      jobsCompleted:
        workforceFilters.jobsCompleted !== "" && workforceFilters.jobsCompleted !== undefined
          ? workforceFilters.jobsCompleted
          : undefined,
      workedWith: workforceFilters.workedWith || undefined,
      backgroundCheck: workforceFilters.backgroundCheck || undefined,
      certifications: workforceFilters.certifications?.value || undefined,
      equipment: workforceFilters.equipment?.value || undefined,
      skillSet: workforceFilters.skillSet?.value || undefined,
    };
  }, [workforceFilters]);

  const {
    isPending: isFetchWorkforceApiLoading,
    data: workforceData,
    refetch: refetchWorkforce,
  } = useQuery({
    queryKey: ["workforce", queryParams, filtersPayload],
    queryFn: () =>
      fetchWorkforce({
        ...queryParams,
        ...filtersPayload,
      }),
    keepPreviousData: true,
  });

  const {
    isPending: isAddEngineerToTalentPoolApiLoading,
    mutateAsync: addEngineerToTalentPoolMutation,
  } = useMutation({
    mutationFn: addEngineerToTalentPool,
    enabled: false,
  });

  const { isPending: isCreateTalentPoolApiLoading, mutateAsync: createTalentPoolMutation } =
    useMutation({
      mutationFn: createTalentPool,
      enabled: false,
    });

  useEffect(() => {
    if (hasTalentPoolWriteAccess) {
      setTalentPoolMode("CREATE");
    } else if (hasWorkForceEngineerUpdateAccess) {
      setTalentPoolMode("UPDATE");
    }
  }, []);

  const engineerProfileLink = (engineerId) => `/user/details/profile/${engineerId}?direct=true`;

  const columns = [
    {
      pinLeft: true,
      field: "id",
      headerName: "ID",
      width: 100,
      renderCell: (params) => (
        <OpenableLink
          as="div"
          to={engineerProfileLink(params.row.id)}
          className="flex items-center w-[206px]"
        >
          <p className="table-font-size font-normal text-primary cursor-pointer ml-4">
            {params.value}
          </p>
        </OpenableLink>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      width: 200,
      renderCell: (params) => (
        <OpenableLink
          as="div"
          to={engineerProfileLink(params.row.id)}
          className="flex items-center cursor-pointer"
        >
          <Avatar src={params.row.profile} sx={{ width: 28, height: 28 }} />
          <p className="ml-3 table-font-size truncate max-w-64 overflow-hidden whitespace-nowrap text-primary capitalize">
            {params.value}
          </p>
        </OpenableLink>
      ),
    },
    /*   {
      field: "billingRates",
      headerName: "Payment Rates",
      width: 300,
      renderCell: (params) => {
        const sortedRates = sortByPaymentType(params.value);
        return sortedRates?.map((rate) => (
          <div className="flex flex-wrap table-font-size w-full" key={rate.id ?? rate.paymentType}>
            <div>
              <p className="h-[14px] text-primary text-center text-[11px] font-medium">
                {rate.rate} {CurrencySymbol[rate.currency] || ""}
              </p>
              <p className="text-center">{PaymentType[rate.paymentType]}</p>
            </div>
          </div>
        ));
      },
    }, */
    {
      field: "freelanceCompany",
      headerName: "Company",
      width: 220,
      renderCell: (params) =>
        params.value ? (
          <OpenableLink
            as="div"
            to={`/workForce/freelanceCompanies/${params.value.id}`}
            className="flex items-center cursor-pointer"
          >
            <p className="table-font-size truncate max-w-64 overflow-hidden whitespace-nowrap text-primary capitalize">
              {params.value.name}{" "}
              <span className="text-[11px] font-300">(ID: {params.value.id})</span>
            </p>
          </OpenableLink>
        ) : (
          <p className="table-font-size">Independent</p>
        ),
    },
    {
      field: "workedWithJobsCount",
      headerName: "Jobs",
      width: 160,
      renderCell: (params) => (
        <div className="table-font-size">
          {/* Marketplace vs My Company split disabled — always show My Company count.
          {params.row.workedWith ? (
            <p className="text-primary font-medium">
              {params.row.workedWithJobsCount}{" "}
              <span className="text-[11px] font-300">(My Company)</span>
            </p>
          ) : (
            <p className="font-medium">
              {params.row.marketplaceJobsCount}
              <span className="text-[11px] font-300"> (Marketplace)</span>
            </p>
          )} */}
          <p className="text-primary font-medium">
            {params.row.workedWithJobsCount}{" "}
            <span className="text-[11px] font-300">(My Company)</span>
          </p>
        </div>
      ),
    },
    {
      field: "certifications",
      headerName: "Certification",
      width: 240,
      renderCell: (params) =>
        params?.value?.map((certificate) => (
          <p className="table-font-size" key={certificate.id}>
            {certificate.qualificationSubType},
          </p>
        )),
    },
    {
      field: "equipments",
      headerName: "Equipments",
      width: 240,
      renderCell: (params) =>
        params?.value?.map((equipment) => (
          <p className="table-font-size" key={equipment.id}>
            {equipment.qualificationSubType},{" "}
          </p>
        )),
    },
    {
      field: "skills",
      headerName: "Skill Sets",
      width: 240,
      renderCell: (params) =>
        params?.value?.map((skill) => (
          <p className="table-font-size" key={skill.id}>
            {skill.subCategory},{" "}
          </p>
        )),
    },
    {
      field: "userTalentPools",
      headerName: "Talent Pools",
      width: 240,
      renderCell: (params) =>
        params?.value?.map((pool) => (
          <p
            className="table-font-size cursor-pointer text-primary"
            key={pool.id}
            onClick={() => navigate(`/workForce/talentPool/engineers/${pool.id}`)}
          >
            {pool.name},{" "}
          </p>
        )),
    },
    {
      field: "rating",
      headerName: "Rating",
      width: 180,
      renderCell: (params) => (
        <div className="flex items-center">
          <BaseRating rating={params.value} />
          <p className="ml-3">{params.value || ""}</p>
        </div>
      ),
    },
    {
      field: "cancellationRate",
      headerName: "Cancellation",
      width: 180,
      renderCell: (params) => <p className="table-font-size">{params.value}%</p>,
    },
    {
      field: "timelinessRate",
      headerName: "Timeliness",
      width: 180,
      renderCell: (params) => <p className="table-font-size">{params.value}%</p>,
    },
    {
      field: "workedWith",
      headerName: "Worked With",
      width: 180,
      renderCell: (params) => (
        <p className="table-font-size">
          {params.value ? (
            <i className="fa fa-check text-sm text-primary bg-[#D8EBF8] rounded-full w-5 text-center"></i>
          ) : (
            <i className="fa fa-xmark text-sm text-red-500 bg-[#FFCDCD] rounded-full w-5 text-center"></i>
          )}
        </p>
      ),
    },
    {
      field: "backgroundChecked",
      headerName: "Background Check",
      width: 180,
      renderCell: (params) => (
        <p className="table-font-size">
          {params.value ? (
            <i className="fa fa-check text-sm text-primary bg-[#D8EBF8] rounded-full w-5 text-center"></i>
          ) : (
            <i className="fa fa-xmark text-sm text-red-500 bg-[#FFCDCD] rounded-full w-5 text-center"></i>
          )}
        </p>
      ),
    },
    {
      field: "createdAt",
      headerName: "Registration Date",
      width: 160,
      renderCell: (params) => (
        <p className="table-font-size cursor-default">
          {`${dayjs(params.value).format("DD-MM-YYYY") || ""}`}
        </p>
      ),
    },
    {
      field: "phoneNumber",
      headerName: "Mobile",
      width: 160,
      renderCell: (params) => {
        const raw = params.value;

        if (!raw) return <p>-</p>;

        const phone = parsePhoneNumberFromString(raw.startsWith("+") ? raw : `+${raw}`);

        if (!phone) {
          return <p className="table-font-size cursor-default">{raw}</p>;
        }

        const formatted = phone.formatInternational().replace(/\s+/g, "-");

        // mask after first 10 characters
        const masked = formatted.slice(0, 10) + ".....";

        return <p className="table-font-size cursor-default">{masked}</p>;
      },
    },
    {
      field: "email",
      headerName: "Email",
      width: 240,
      renderCell: (params) => <p className="table-font-size cursor-default">{params.value}</p>,
    },
    {
      field: "personalAddress",
      headerName: "Location",
      width: 220,
      renderCell: (params) => (
        <p className="table-font-size cursor-default">
          {`${params.row?.personalAddress?.addressLine1 || ""}`}
        </p>
      ),
    },
    {
      field: "city",
      headerName: "City",
      width: 130,
      renderCell: (params) => (
        <p className="table-font-size cursor-default">{`${
          params.row?.personalAddress?.city || ""
        }`}</p>
      ),
    },
    {
      field: "state",
      headerName: "State",
      width: 130,
      renderCell: (params) => (
        <p className="table-font-size cursor-default">{`${
          params.row?.personalAddress?.state || ""
        }`}</p>
      ),
    },
    {
      field: "zipcode",
      headerName: "Zip / Postal Code",
      width: 150,
      renderCell: (params) => (
        <p className="table-font-size cursor-default">{`${
          params.row?.personalAddress?.zipCode || ""
        }`}</p>
      ),
    },
    {
      field: "country",
      headerName: "Country",
      width: 130,
      renderCell: (params) => (
        <p className="table-font-size">{params.row?.personalAddress?.country || ""}</p>
      ),
    },
    {
      field: "lastActive",
      headerName: "Last Active",
      width: 140,
      renderCell: (params) => <p className="table-font-size">{dayjs(params.value).fromNow()}</p>,
    },
  ];

  const handlePaginationChange = (newPaginationModel) => {
    updateFilter({
      filterName: "pagination",
      data: {
        page: newPaginationModel.page,
        pageSize: newPaginationModel.pageSize,
      },
    });
  };

  const debouncedSearchRef = useRef(
    debounce((value) => {
      updateFilter({ filterName: "search", data: value });
    }, 500),
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    debouncedSearchRef.current(e.target.value);
  };

  const handleCreateTalentPool = async () => {
    try {
      const newTalentPool = await createTalentPool({ name: talentPoolName });
      setTalentPoolName("");
      refetchTalentPool();
      return newTalentPool;
    } catch (error) {
      dispatch(
        showToast({
          message: error.response.data.message,
          type: "error",
        }),
      );
    }
  };

  const handleAddEngineerToTalentPool = async (talentPoolId) => {
    try {
      const payload = {
        talentPoolId,
        userIds: selectedEngineers,
      };
      await addEngineerToTalentPoolMutation(payload);
      navigate(`/workForce/talentPool/engineers/${talentPoolId}`);
      setAddToTalentPoolPopoverOpen(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error.response.data.message,
          type: "error",
        }),
      );
    }
  };

  const handleClick = (event) => {
    setAddToTalentPoolPopoverOpen(event.currentTarget);
  };

  const handleClose = () => {
    setAddToTalentPoolPopoverOpen(null);
  };

  const handleCreateNewTalentPoolAndAddEngineers = async () => {
    try {
      const talentPoolCreated = await handleCreateTalentPool();
      await handleAddEngineerToTalentPool(talentPoolCreated?.data?.id);
    } catch (error) {}
  };

  const handleAddToTalentPoolButtonn = async () => {
    if (talentPoolMode === "UPDATE") {
      await handleAddEngineerToTalentPool(talentPoolName);
    } else {
      await handleCreateNewTalentPoolAndAddEngineers();
    }
  };
  const appliedFilters = useMemo(() => {
    const filters = [];
    const wf = workforceFilters;

    if (wf?.fullName) {
      filters.push({ prefix: "Name", value: wf.fullName, key: "fullName" });
    }
    if (wf?.country?.label) {
      filters.push({
        prefix: "Country",
        value: wf.country.label,
        key: "country",
      });
    }
    if (wf?.city?.label) {
      filters.push({ prefix: "City", value: wf.city.label, key: "city" });
    }
    if (wf?.talentPool?.label) {
      filters.push({
        prefix: "Talent Pool",
        value: wf.talentPool.label,
        key: "talentPool",
      });
    }
    if (wf?.jobsCompleted) {
      filters.push({
        prefix: "Jobs Completed",
        value: wf.jobsCompleted,
        key: "jobsCompleted",
      });
    }
    if (wf?.workedWith) {
      filters.push({
        prefix: "Worked With",
        value: wf.workedWith,
        key: "workedWith",
      });
    }
    if (wf?.backgroundCheck) {
      filters.push({
        prefix: "Background Check",
        value: wf.backgroundCheck,
        key: "backgroundCheck",
      });
    }
    if (wf?.certifications?.label) {
      filters.push({
        prefix: "Certification",
        value: wf.certifications.label,
        key: "certifications",
      });
    }
    if (wf?.equipment?.label) {
      filters.push({
        prefix: "Equipment",
        value: wf.equipment.label,
        key: "equipment",
      });
    }
    if (wf?.skillSet?.label) {
      filters.push({
        prefix: "Skill Set",
        value: wf.skillSet.label,
        key: "skillSet",
      });
    }

    return filters;
  }, [workforceFilters]);

  const hasAppliedFilters = appliedFilters.length > 0;

  const handleRemoveFilter = (key) => {
    if (key === "pagination") {
      updateFilter({
        filterName: "pagination",
        data: { page: 0, pageSize: workforceFilters.pagination.pageSize },
      });
      return;
    }

    updateFilter({ filterName: key, data: "" });
    updateFilter({
      filterName: "pagination",
      data: { page: 0, pageSize: workforceFilters.pagination.pageSize },
    });
    setFiltersChanged(true);
  };

  useEffect(() => {
    setSearch(workforceFilters.search || "");
  }, [workforceFilters.search]);

  return (
    <BaseWrapper>
      <div className="bg-white rounded-lg px-5 py-2 shadow-md mt-2">
        <div className="flex justify-between items-center w-full">
          <div className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary">
              <i className="fa-light fa-filter text-white text-sm"></i>
            </span>
            <p
              className="text-primary ml-2 cursor-pointer"
              onClick={() => setFiltersDrawerOpen(true)}
            >
              Add Filter
            </p>
          </div>
          <div className="flex-1 min-w-0 pl-3 overflow-x-auto whitespace-nowrap py-1 scrollbar">
            <div className="flex gap-2">
              {appliedFilters.map((f) => {
                return (
                  <div
                    key={(f.key || "") + (f.value || "") + (f.id || "")}
                    className="flex-shrink-0 flex items-center bg-[#EAF1FF] text-sm font-medium rounded-md px-3 py-1"
                  >
                    <span className="text-primary">{f.prefix}:</span>&nbsp;
                    <p className="text-primary text-sm mx-1">{f.value}</p>
                    <button
                      onClick={() => handleRemoveFilter(f.key, f.id)}
                      className="ml-2 text-primary hover:text-red-500"
                      aria-label={`Remove ${f.prefix} ${f.value}`}
                    >
                      <i className="fas fa-times text-xs"></i>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          <div className=" w-[28%]">
            <BaseTextField
              placeholder={`Search`}
              value={search}
              onChange={(e) => handleSearchChange(e)}
              startAdornment={<i className="fa-light fa-magnifying-glass text-lg"></i>}
            />
          </div>
        </div>
      </div>

      {selectedEngineers.length > 0 &&
        (hasWorkForceEngineerUpdateAccess || hasTalentPoolWriteAccess) && (
          <BaseButton
            className="bg-white text-default py-[4px] px-[24px] mt-2"
            onClick={handleClick}
          >
            Move to Talent Pool
          </BaseButton>
        )}
      <BasePopover
        open={!!addToTalentPoolPopoverOpen}
        anchorEl={addToTalentPoolPopoverOpen}
        onClose={handleClose}
      >
        <div className="w-[360px]">
          <div className="p-4 ">
            <h6 className="pb-1 border-b">Add to Talent Pool</h6>
            <RadioGroup
              name="talentPool"
              value={talentPoolMode}
              onChange={(e) => {
                setTalentPoolMode(e.target.value);
              }}
              className="text-xs"
            >
              {hasWorkForceEngineerUpdateAccess && (
                <div>
                  <BaseRadio
                    label={<p className="text-xs">Add to Existing Talent Pool</p>}
                    value="UPDATE"
                    name="talentPool"
                    sx={{
                      "& .MuiSvgIcon-root": {
                        color: "#0F40AE",
                      },
                    }}
                  />
                </div>
              )}
              {hasTalentPoolWriteAccess && (
                <div>
                  <BaseRadio
                    label={<p className="text-xs">Create new Talent Pool</p>}
                    value="CREATE"
                    name="talentPool"
                    sx={{
                      "& .MuiSvgIcon-root": {
                        color: "#0F40AE",
                      },
                    }}
                  />
                </div>
              )}
            </RadioGroup>

            {talentPoolMode === "UPDATE" && hasWorkForceEngineerUpdateAccess && (
              <div className="mt-2">
                <BaseAutoSelect
                  placeholder="Select Your Talent Pool"
                  label="Talent Pool"
                  isDefaultField={false}
                  labelShrink={false}
                  required={true}
                  options={formattedTalentPoolOptions}
                  onChange={(e) => {
                    setTalentPoolName(e.target.value);
                  }}
                />
              </div>
            )}
            {talentPoolMode === "CREATE" && hasTalentPoolWriteAccess && (
              <div className="mt-2">
                <BaseTextField
                  placeholder="Enter new Talent Pool name"
                  label="New Talent Pool"
                  isDefaultField={false}
                  labelShrink={false}
                  required={true}
                  optional={false}
                  value={talentPoolName}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTalentPoolName(value.trim());
                  }}
                />
              </div>
            )}
          </div>
          <div
            className="flex justify-center py-4"
            style={{
              boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
            }}
          >
            <BaseButton
              onClick={() => setAddToTalentPoolPopoverOpen(null)}
              color="light"
              className="py-[3px] px-[24px] bg-light text-default hover:text-primary mr-4"
            >
              Cancel
            </BaseButton>
            <BaseButton
              isLoading={isCreateTalentPoolApiLoading || isAddEngineerToTalentPoolApiLoading}
              color="primary"
              disabled={talentPoolName?.length < 1}
              className="py-[3px] px-[28px]"
              onClick={handleAddToTalentPoolButtonn}
            >
              Save
            </BaseButton>
          </div>
        </div>
      </BasePopover>

      <div className="mt-2">
        <BaseDataTableV2
          className="max-h-[calc(100vh-300px)]"
          rowHeight={38}
          columns={columns.filter(
            (c) =>
              !(isScoped && c.field === "freelanceCompany") &&
              !(isCustomer && CUSTOMER_HIDDEN_WORKFORCE_FIELDS.includes(c.field)),
          )}
          data={workforceData?.data || []}
          isLoading={isFetchWorkforceApiLoading}
          disableColumnResize={false}
          disableRowSelectionOnClick
          checkboxSelection
          onRowSelectionModelChange={(engineerIds) => {
            setSelectedEngineers(engineerIds);
          }}
          hideFooter={false}
          pagination
          rowCount={workforceData?.total}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={workforceFilters.pagination}
          onPaginationModelChange={handlePaginationChange}
          paginationMode="server"
        />
      </div>
      {filtersDrawerOpen && (
        <WorkForceFilters
          open={filtersDrawerOpen}
          onClose={() => setFiltersDrawerOpen(false)}
          fetchWorkforce={() => refetchWorkforce()}
          isLoading={isFetchWorkforceApiLoading}
          setFiltersChanged={setFiltersChanged}
          talentPoolData={formattedTalentPoolOptions}
          filterType={isScoped ? "freelanceCompanyWorkforce" : "workforce"}
        />
      )}
    </BaseWrapper>
  );
};

export default WorkForceTable;
